import React from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export const SITE_URL = "https://laconciergerierouen.com";
export const DEFAULT_OG = "https://customer-assets-gfyr7b9c.emergentagent.net/job_booking-nj-preview/artifacts/uqbgs8qm_498595032.webp";

// React 19 hoists <title>/<meta>/<link>/<script> rendered here into the document <head>.
export default function Seo({ title, description, canonical, image = DEFAULT_OG, jsonLd, noindex = false }) {
  // No-trailing-slash policy (matches flat build/<route>.html on Cloudflare); root keeps its slash.
  const url = `${SITE_URL}${canonical || "/"}`;
  const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:site_name" content="NJ Conciergerie" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>
      ))}
    </>
  );
}

// Reusable LocalBusiness JSON-LD for NJ Conciergerie.
export const localBusinessLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "NJ Conciergerie",
  "description": "Conciergerie Airbnb et gestion de location courte durée à Rouen. Gestion complète Airbnb & Booking pour propriétaires.",
  "url": SITE_URL,
  "telephone": "+33666934267",
  "email": "contact@laconciergerierouen.com",
  "image": DEFAULT_OG,
  "priceRange": "€€",
  "openingHours": "Mo-Su 09:00-18:00",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "22 boulevard de la Marne",
    "addressLocality": "Rouen",
    "postalCode": "76000",
    "addressCountry": "FR"
  },
  "areaServed": ["Rouen", "Bois-Guillaume", "Mont-Saint-Aignan", "Rouen Rive Droite", "Rouen Rive Gauche"],
};

// LocalBusiness JSON-LD with phone/email/address pulled from the editable CMS settings.
export function useLocalBusinessLd() {
  const s = useSiteSettings();
  const ld = { ...localBusinessLd };
  if (s.phone_intl) ld.telephone = s.phone_intl;
  if (s.email) ld.email = s.email;
  if (s.address) {
    const parts = s.address.split(",").map((x) => x.trim());
    const m = (parts[1] || "").match(/(\d{5})\s+(.+)/);
    ld.address = {
      "@type": "PostalAddress",
      streetAddress: parts[0] || localBusinessLd.address.streetAddress,
      postalCode: m ? m[1] : localBusinessLd.address.postalCode,
      addressLocality: m ? m[2] : localBusinessLd.address.addressLocality,
      addressCountry: "FR",
    };
  }
  return ld;
}
